<template>
  <div class="container">
    <div class="row">
      <div class="col-12">

        <!-- Toggle Buttons -->
        <div class="form-group mb-3">
          <label>Please select the relevant supporting document for each input. For employment contracts, choose the signed contract from the dropdown list. If no contracts are available, request the employee to sign and submit the contract or assign the necessary forms first.</label>
          <div>
            <button class="btn btn-outline-primary mr-2" @click.prevent="selectedChecklistType = 'limited'">Show Limited</button>
            <button class="btn btn-outline-secondary" @click.prevent="selectedChecklistType = 'full'">Show Full</button>
            <button class="btn btn-link text-danger" @click.prevent="selectedChecklistType = null">Hide All</button>
          </div>
        </div>

        <template v-if="selectedChecklistType === 'limited'">
        <!-- Limited Screening Checklist -->
        <h4 class="mb-2">Limited Screening Checklist</h4>
        <el-collapse v-model="activeCollapseLimited" accordion>
          <draggable
              tag="div"
              :list="limitedChecklist"
              item-key="check_point"
              handle=".drag-handle"
              class="w-100"
              :animation="200"
              @end="onSortEnd"
          >
            <el-collapse-item
                v-for="(checkList, index) in limitedChecklist"
                :key="checkList.check_point"
                :name="checkList.check_point"
                class="my-1 checklist-collapse-item"
            >
              <template #title>
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <strong class="drag-handle mr-2" style="cursor: move;">☰</strong>
                  <label class="mb-0 font-weight-bold text-left w-100">
                    {{ checkList.check_point }}
                  </label>
                </div>
              </template>

              <!-- Date and Remarks -->
              <div class="form-row">
                <div class="form-group col-md-4">
                  <label class="font-weight-bold d-block">{{ $t('date') }}</label>
                  <el-date-picker
                      v-model="checkList.date"
                      type="date"
                      placeholder="Select date"
                      format="dd-MM-yyyy"
                      value-format="yyyy-MM-dd"
                      class="w-100"
                      :picker-options="getPickerOptions(checkList.screening_type)"
                  />
                </div>

                <div class="form-group col-md-4">
                  <label class="font-weight-bold d-block">{{ $t('Remarks') }}</label>
                  <app-input type="text" :placeholder="$t('Remarks')" v-model="checkList.comments" :required="true" />
                </div>
              </div>

              <!-- File Selector -->
              <div
                  v-if="Array.isArray(checkList.screening_checkList_files) && availableFiles(checkList).length"
                  v-for="(file, fileIndex) in checkList.screening_checkList_files"
                  :key="`${checkList.id}-file-${fileIndex}`"
                  class="form-row align-items-center mb-2"
              >
                <div class="form-group col-md-4 mb-0">
                  <el-select
                      v-model="checkList.screening_checkList_files[fileIndex]"
                      :placeholder="$t('screeningCheckListFiles')"
                      :ref="`fileSelect-${checkList.id}-${fileIndex}`"
                      filterable
                      clearable
                      class="w-100"
                  >
                    <el-option
                        v-for="option in availableFiles(checkList)"
                        :key="option.id"
                        :label="option.value"
                        :value="option.id"
                        :disabled="option.disabled"
                    />
                  </el-select>
                </div>

                <div class="form-group col-md-4 mb-0 d-flex align-items-center">
          <span v-if="getFileById(file)">
            <a :href="getFileUrl(getFileById(file))" target="_blank" class="text-primary font-weight-bold ml-2">
              <i class="el-icon-view mr-1"></i> {{ getFileLabel(file) }}
            </a>
          </span>
                </div>
              </div>

              <!-- Form Response Selector -->
              <div v-if="submittedForms && submittedForms.length === 0" class="form-row mb-2">
                <div class="col-12">
                  <p class="text-danger small mb-0">{{ $t('Response has not been received yet from applicant.') }}</p>
                </div>
              </div>
              <div
                  v-else-if="Array.isArray(checkList.task_form_status_ids) && availableForms(checkList).length"
                  v-for="(formId, formIndex) in checkList.task_form_status_ids"
                  :key="`${checkList.id}-form-${formIndex}`"
                  class="form-row align-items-center mb-2"
              >
                <div class="form-group col-md-4 mb-0">
                  <el-select
                      v-model="checkList.task_form_status_ids[formIndex]"
                      :placeholder="$t('Submitted Forms')"
                      :ref="`formSelect-${checkList.id}-${formIndex}`"
                      filterable
                      clearable
                      class="w-100"
                  >
                    <el-option
                        v-for="option in availableForms(checkList)"
                        :key="option.id"
                        :label="option.value"
                        :value="option.id"
                        :disabled="option.disabled"
                    />
                  </el-select>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="form-row mb-2" v-if="!['SIA Licence Web check', 'Consumer Information Check', 'Financial Sanctions Check'].includes(checkList.check_point)">
                <div class="col-md-4">
                  <button class="btn btn-primary btn-sm" @click="addFileSelection(checkList)" type="button">
                    + Add File
                  </button>
                </div>
                <div class="col-md-4">
                  <button class="btn btn-primary btn-sm" @click="addFormSelection(checkList)" type="button">
                    + Add Form Response
                  </button>
                </div>
              </div>

              <!-- Sample File -->
              <div v-if="checkList.sample_file" class="form-row">
                <div class="col-md-6">
                  <a :href="'/sample-documents/' + checkList.sample_file" target="_blank" class="text-primary">
                    Download Sample File: {{ checkList.sample_file }}
                  </a>
                </div>
              </div>

            </el-collapse-item>
          </draggable>
        </el-collapse>

       </template>
        <template v-if="selectedChecklistType === 'full'">


        <!-- Full Screening Checklist -->
        <h4 class="mt-4 mb-2">Full Screening Checklist</h4>
        <el-collapse v-model="activeCollapseFull" accordion>
          <draggable
              tag="div"
              :list="fullChecklist"
              item-key="check_point"
              handle=".drag-handle"
              class="w-100"
              :animation="200"
              @end="onSortEnd"
          >
            <el-collapse-item
                v-for="(checkList, index) in fullChecklist"
                :key="checkList.check_point"
                :name="checkList.check_point"
                class="my-1 checklist-collapse-item"
            >
              <template #title>
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <strong class="drag-handle mr-2" style="cursor: move;">☰</strong>
                  <label class="mb-0 font-weight-bold text-left w-100">
                    {{ checkList.check_point }}
                  </label>
                </div>
              </template>

              <!-- Date and Remarks -->
              <div class="form-row">
                <div class="form-group col-md-4">
                  <label class="font-weight-bold d-block">{{ $t('date') }}</label>
                  <el-date-picker
                      v-model="checkList.date"
                      type="date"
                      placeholder="Select date"
                      format="dd-MM-yyyy"
                      value-format="yyyy-MM-dd"
                      class="w-100"
                      :picker-options="getPickerOptions(checkList.screening_type)"
                  />
                </div>

                <div class="form-group col-md-4">
                  <label class="font-weight-bold d-block">{{ $t('Remarks') }}</label>
                  <app-input type="text" :placeholder="$t('Remarks')" v-model="checkList.comments" :required="true" />
                </div>
              </div>

              <!-- File Selector -->
              <div
                  v-if="Array.isArray(checkList.screening_checkList_files) && availableFiles(checkList).length"
                  v-for="(file, fileIndex) in checkList.screening_checkList_files"
                  :key="`${checkList.id}-file-${fileIndex}`"
                  class="form-row align-items-center mb-2"
              >
                <div class="form-group col-md-4 mb-0">
                  <el-select
                      v-model="checkList.screening_checkList_files[fileIndex]"
                      :placeholder="$t('screeningCheckListFiles')"
                      :ref="`fileSelect-${checkList.id}-${fileIndex}`"
                      filterable
                      clearable
                      class="w-100"
                  >
                    <el-option
                        v-for="option in availableFiles(checkList)"
                        :key="option.id"
                        :label="option.value"
                        :value="option.id"
                        :disabled="option.disabled"
                    />
                  </el-select>
                </div>

                <div class="form-group col-md-4 mb-0 d-flex align-items-center">
          <span v-if="getFileById(file)">
            <a :href="getFileUrl(getFileById(file))" target="_blank" class="text-primary font-weight-bold ml-2">
              <i class="el-icon-view mr-1"></i> {{ getFileLabel(file) }}
            </a>
          </span>
                </div>
              </div>

              <!-- Form Response Selector -->
              <div v-if="submittedForms && submittedForms.length === 0" class="form-row mb-2">
                <div class="col-12">
                  <p class="text-danger small mb-0">{{ $t('Response has not been received yet from applicant.') }}</p>
                </div>
              </div>
              <div
                  v-else-if="Array.isArray(checkList.task_form_status_ids) && availableForms(checkList).length"
                  v-for="(formId, formIndex) in checkList.task_form_status_ids"
                  :key="`${checkList.id}-form-${formIndex}`"
                  class="form-row align-items-center mb-2"
              >
                <div class="form-group col-md-4 mb-0">
                  <el-select
                      v-model="checkList.task_form_status_ids[formIndex]"
                      :placeholder="$t('Submitted Forms')"
                      :ref="`formSelect-${checkList.id}-${formIndex}`"
                      filterable
                      clearable
                      class="w-100"
                  >
                    <el-option
                        v-for="option in availableForms(checkList)"
                        :key="option.id"
                        :label="option.value"
                        :value="option.id"
                        :disabled="option.disabled"
                    />
                  </el-select>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="form-row mb-2" v-if="!['SIA Licence Web check', 'Consumer Information Check', 'Financial Sanctions Check'].includes(checkList.check_point)">
                <div class="col-md-4">
                  <button class="btn btn-primary btn-sm" @click="addFileSelection(checkList)" type="button">
                    + Add File
                  </button>
                </div>
                <div class="col-md-4">
                  <button class="btn btn-primary btn-sm" @click="addFormSelection(checkList)" type="button">
                    + Add Form Response
                  </button>
                </div>
              </div>

              <!-- Sample File -->
              <div v-if="checkList.sample_file" class="form-row">
                <div class="col-md-6">
                  <a :href="'/sample-documents/' + checkList.sample_file" target="_blank" class="text-primary">
                    Download Sample File: {{ checkList.sample_file }}
                  </a>
                </div>
              </div>

            </el-collapse-item>
          </draggable>
        </el-collapse>

        </template>
        <!-- Submit Button -->
        <div class="mt-3 text-right">
          <button @click.prevent="saveScreeningChecklist" class="btn btn-success">Update Checklist</button>
        </div>
      </div>
      </div>

    <!-- File Preview Dialog -->
    <!-- (Modal removed as per user request to open in new tab instead) -->
</div>
</template>





<script>
import draggable from "vuedraggable";
import {FormMixin} from "@/core/mixins/form/FormMixin";
import CoreLibrary from "@/core/helpers/CoreLibrary";
import {Candidate} from "@Views/Screening/utility/apiAgent";
import {APPLICANT_FILES, UPDATE_CHECKLIST_ORDER} from "@/app/Config/ScreeningAppUrl";

export default {
  name: "InputDetails",
  components: { draggable },
  mixins: [FormMixin],
  extends: CoreLibrary,
  props: {
    applicantScreeningCheckpoint: {
      type: Array,
      default: () => []
    },
    candidateId: Number,
    companyId: Number,
    submittedForms: Array,
    applicantFiles: Array,
    screeningSchedule: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      selectedChecklistType: null, // 'limited' or 'full'
      activeCollapse: null, // or the first checklist item ID if you want one open by default
      dialogVisible: false,
      dialogImage: '',
      activeCollapseLimited: null,
      activeCollapseFull: null
    };
  },
  computed: {
    limitedChecklist() {
      const filtered = this.applicantScreeningCheckpoint.filter(item => item.screening_type === 'limited');
      const map = new Map();
      filtered.forEach(it => {
        if (!map.has(it.check_point)) {
          // Filter out invalid IDs that might remain after a file/form is deleted
          if (Array.isArray(it.screening_checkList_files)) {
            it.screening_checkList_files = it.screening_checkList_files.filter(id =>
                id === "" || id === null || this.mappedApplicantFiles.some(f => f.id === Number(id))
            );
          }
          if (Array.isArray(it.task_form_status_ids)) {
            it.task_form_status_ids = it.task_form_status_ids.filter(id =>
                id === "" || id === null || this.mappedSubmittedForms.some(f => f.id === Number(id))
            );
          }
          map.set(it.check_point, it);
        }
      });
      return Array.from(map.values());
    },
    fullChecklist() {
      const filtered = this.applicantScreeningCheckpoint.filter(item => item.screening_type === 'full');
      const map = new Map();
      filtered.forEach(it => {
        if (!map.has(it.check_point)) {
          // Filter out invalid IDs that might remain after a file/form is deleted
          if (Array.isArray(it.screening_checkList_files)) {
            it.screening_checkList_files = it.screening_checkList_files.filter(id =>
                id === "" || id === null || this.mappedApplicantFiles.some(f => f.id === Number(id))
            );
          }
          if (Array.isArray(it.task_form_status_ids)) {
            it.task_form_status_ids = it.task_form_status_ids.filter(id =>
                id === "" || id === null || this.mappedSubmittedForms.some(f => f.id === Number(id))
            );
          }
          map.set(it.check_point, it);
        }
      });
      return Array.from(map.values());
    },
    mappedSubmittedForms() {
      return this.submittedForms.map(form => ({
        id: form.id,
        value: form.title
      }));
    },
    mappedApplicantFiles() {
      return this.applicantFiles
        .filter(file => (file.type || '').toLowerCase() !== 'right to work')
        .map(file => ({
          id: file.id,
          value: `${file.file_name} (${file.type})`
        }));
    }
  },
  methods: {
    getPickerOptions(type) {
      return {
        disabledDate: (time) => {
          return false;
        }
      };
    },
    availableForms(checkList) {
      const selectedForms = new Set(checkList.task_form_status_ids.map(Number));
      return this.mappedSubmittedForms.map(form => ({
        ...form,
        disabled: selectedForms.has(form.id)
      }));
    },

    getFileLabel1(fileId) {
      const match = this.mappedApplicantFiles.find(file => file.id === Number(fileId));
      return match ? match.value : "";
    },
    addFileSelection(checkList) {
      if (!Array.isArray(checkList.screening_checkList_files)) {
        this.$set(checkList, "screening_checkList_files", []);
      }
      checkList.screening_checkList_files.push("");
    },
    addFormSelection(checkList) {
      if (!Array.isArray(checkList.task_form_status_ids)) {
        this.$set(checkList, "task_form_status_ids", []);
      }
      checkList.task_form_status_ids.push("");
    },
    removeFile(checkList, fileIndex) {
      checkList.screening_checkList_files.splice(fileIndex, 1);
    },
    availableFiles(checkList) {
      const selectedFiles = new Set(checkList.screening_checkList_files.map(Number));
      return this.mappedApplicantFiles.map(file => ({
        ...file,
        disabled: selectedFiles.has(file.id)
      }));
    },
    async onSortEnd() {
      try {
        const payload = this.applicantScreeningCheckpoint.map((item, index) => ({
          id: item.id,
          order: index + 1
        }));

        await this.axiosPost({
          url: UPDATE_CHECKLIST_ORDER,
          data: { items: payload }
        }).then(res => {
          this.$toastr.s(res.data.message);
        }).catch(err => {
          this.$toastr.s(err.response.error);
        });
      } catch (error) {
        console.error(error);
        this.$toastr.e("Failed to update checklist order");
      }
    },
    saveScreeningChecklist() {
      const cleanedChecklist = JSON.parse(JSON.stringify(this.applicantScreeningCheckpoint)).map(item => {

        const arrayFields = ['screening_checkList_files', 'task_form_status_ids'];

        arrayFields.forEach(field => {
          if (Array.isArray(item[field])) {
            item[field] = item[field]
                .filter(value => value !== '' && value !== null) // Remove empty/null
                .map(value => parseInt(value)); // Convert to int
          }
        });

        return item;
      });

      Candidate.postScreeningChecklist(this.candidateId, cleanedChecklist)
          .then(res => {
            this.$toastr.s(res.data.message);
          })
          .catch(err => {
            this.$toastr.e(err.response?.data?.message || 'Failed to update checklist.');
          });
    },
    getFileById(fileId) {
      return this.applicantFiles.find(file => file.id === Number(fileId));
    },
    getFileLabel(fileId) {
      const file = this.getFileById(fileId);
      return file ? `${file.file_name} (${file.type})` : "";
    },
    getFileUrl(file) {
      if (file && file.file_path) {
        return `/storage/${file.file_path.replace(/^storage\//, '')}`;
      }
      return '#';
    },
    showImage(file) {
      if (file && file.file_path) {
        this.dialogImage = `/storage/${file.file_path.replace(/^storage\//, '')}`;
        this.dialogVisible = true;
      }
    },
    isImage(path) {
      return /\.(jpeg|jpg|png|gif|bmp|webp)$/i.test(path);
    },
    isPDF(path) {
      return /\.pdf$/i.test(path);
    },

    getApplicantFiles(){
      let url = `${APPLICANT_FILES}`;
      url +=  `?id=${this.applicantScreeningCheckpoint.applicant_id}&per_page=200`;
      this.axiosGet(url)
          .then((response) => {
            const files = response.data.data;
            this.applicantFiles = files.map(function(file) {
              return {
                id: file.id, // Use file.id for the id property
                value: `${file.file_name} (${file.type})` // Combine file_name and file_type for the value
              };
            });
          })
          .catch(({ response }) => {

          })
          .finally(() => {

          });
    },
  },
  created(){
    this.$hub.$emit("candidateDetailActiveIndex", 10);

  },
};
</script>

<style>
/* Highlight on hover */
.checklist-collapse-item .el-collapse-item__header:hover {
  background-color: #f5faff !important;
  border-left: 3px solid #007bff;
  cursor: pointer;
}

/* Highlight when active */
.checklist-collapse-item.is-active .el-collapse-item__header {
  background-color: #e6f0ff !important;
  border-left: 3px solid #007bff;
}

/* Smooth transition */
.el-collapse-item__header {
  transition: background-color 0.3s ease, border-left 0.3s ease;
}

/* Other styles (optional, retained from your original) */
.checklist-item {
  background: #f9f9f9;
  border-radius: 6px;
}
.checklist-item label {
  font-weight: 500;
  text-align: left;
}
.checklist-item .form-group {
  margin-bottom: 0.75rem;
}
</style>
